import { RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { WithDeletedBy } from '@/common/soft-delete';
import { type WorkPackage, workPackageStatusLabel } from '@/common/work-package';
import { DeletedBadge, DeletedOnIcon } from '@/components/soft-delete/DeletedInfo';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn, formatCurrencyWithCents } from '@/lib/utils';

export type WorkPackageCardProps = {
	workpackage: WithDeletedBy<WorkPackage>;
	onCardClick?: (workpackageId: string) => void;
	/** Deleted-items view: the card shows when it was deleted and a Restore button; it opens read-only. */
	onRestore?: (workpackage: WorkPackage) => void;
};
export function WorkPackageCard({ workpackage, onCardClick, onRestore }: WorkPackageCardProps) {
	const { t } = useTranslation();
	const deletedView = !!onRestore;
	return (
		<Card
			key={workpackage.title}
			className={cn(
				'bg-card text-card-foreground border gap-4 rounded-lg p-4 transition-all group',
				'hover:border-primary hover:shadow-md cursor-pointer',
			)}
			onClick={() => onCardClick?.(workpackage.id as string)}
		>
			<div className="flex justify-between items-start pb-0">
				<h3 className="font-medium group-hover:text-blue-600 transition-colors flex items-center gap-2">
					{workpackage.title}
					{deletedView && <DeletedBadge />}
					{deletedView && <DeletedOnIcon deletedAt={workpackage.deletedAt} deletedBy={workpackage.deletedBy} />}
				</h3>
				<Badge
					variant="secondary"
					className={cn('', {
						'bg-yellow-100 text-yellow-700 border-yellow-200': workpackage.status === 'Pending',
						'bg-blue-100 text-blue-700 border-blue-200': workpackage.status === 'Assigned',
						'bg-green-100 text-green-700 border-green-200': workpackage.status === 'Completed',
					})}
				>
					{workPackageStatusLabel(t, workpackage.status)}
				</Badge>
			</div>
			<div className="border-t"></div>
			<CardContent className="px-0 space-y-2 text-sm">
				<div className="flex justify-between text-muted-foreground">
					<span>{t('finance.card.netTotal')}</span>
					<span>{formatCurrencyWithCents(workpackage.netAmount)}</span>
				</div>
				<div className="flex justify-between text-muted-foreground">
					<span>{t('finance.card.vat', { rate: workpackage.vat })}</span>
					<span>{formatCurrencyWithCents(workpackage.taxAmount)}</span>
				</div>
				<div className="flex justify-between font-bold pt-2 border-t">
					<span>{t('finance.card.grossTotal')}</span>
					<span>{formatCurrencyWithCents(workpackage.grossAmount)}</span>
				</div>
			</CardContent>
			{onRestore && (
				<div className="flex justify-end">
					<Button
						variant="outline"
						size="sm"
						onClick={(event) => {
							// The card opens the package; this only restores
							event.stopPropagation();
							onRestore(workpackage);
						}}
					>
						<RotateCcw />
						{t('common.actions.restore')}
					</Button>
				</div>
			)}
		</Card>
	);
}
